import { falMagnifyingGlass } from 'shared/@generated/icons/falMagnifyingGlass';
import { falCheck } from 'shared/@generated/icons/falCheck';
import { falTimes } from 'shared/@generated/icons/falTimes';
import { fasMagnifyingGlass } from 'shared/@generated/icons/fasMagnifyingGlass';
import { clsx } from 'clsx';
import { type ReactNode, type Ref, useEffect, useState } from 'react';
import { Pressable, ScrollView, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Breed } from 'shared/@generated/models';
import { Button } from 'shared/components/button';
import { TextInput, type TextInputRef } from 'shared/components/form/text-input';
import { Icon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import { Loader } from 'shared/components/loader';
import { Text } from 'shared/components/text';
import { useBreedSelectionHistory } from 'shared/library/use-breed-selection-history';

export type BreedPickerPanelProps = {
  query: string;
  setQuery: (value: string) => void;
  breeds: Breed[];
  allBreeds: Breed[];
  loading: boolean;
  selected: (string | number)[];
  multiple?: boolean;
  allowQuery?: boolean;
  maxSelections?: number;
  error?: boolean;
  onRetry?: () => void;
  onToggle: (breed: Breed) => void;
  onRemove: (value: string | number) => void;
  onSelectQuery?: (query: string) => void;
  getQueryLabel?: (query: string) => string;
  optionTestID?: (breed: Breed) => string;
  inputTestID?: string;
  queryOptionTestID?: string;
  retryTestID?: string;
  recordHistory?: boolean;
  searchInputRef?: Ref<TextInputRef>;
  autoFocus?: boolean;
  placeholder?: string;
};

export function BreedPickerPanel({
  query,
  setQuery,
  breeds,
  allBreeds,
  loading,
  selected,
  multiple = true,
  allowQuery = true,
  maxSelections,
  error = false,
  onRetry,
  onToggle,
  onRemove,
  onSelectQuery,
  getQueryLabel,
  optionTestID,
  inputTestID = 'breed-input-text',
  queryOptionTestID = 'breed-filter-query-option',
  retryTestID = 'breed-picker-retry',
  recordHistory = true,
  searchInputRef,
  autoFocus = false,
  placeholder,
}: BreedPickerPanelProps) {
  const { t } = useTranslation(['components', 'common']);
  const { history, remember, forget } = useBreedSelectionHistory();
  const searchPlaceholder =
    placeholder ??
    t(
      multiple
        ? 'components:form.breed_input.multiple_placeholder'
        : 'components:form.breed_input.placeholder',
    );
  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;
  const [visibleCount, setVisibleCount] = useState(40);
  const pinnedValues = isSearching ? [] : Array.from(new Set(selected));
  const historyValues = isSearching
    ? []
    : history.filter(
        (value) =>
          !pinnedValues.some((pinned) =>
            typeof pinned === 'string' && typeof value === 'string'
              ? pinned.trim().toLocaleLowerCase() === value.trim().toLocaleLowerCase()
              : pinned === value,
          ) &&
          (typeof value === 'number' ? breeds.some((breed) => breed.id === value) : allowQuery),
      );
  const popularBreeds = isSearching
    ? []
    : breeds
        .filter((breed) => !pinnedValues.includes(breed.id) && !historyValues.includes(breed.id))
        .sort(
          (a, b) =>
            (b.active_pets_count ?? 0) - (a.active_pets_count ?? 0) ||
            a.name.localeCompare(b.name) ||
            a.id - b.id,
        )
        .slice(0, 10);
  const resultBreeds = isSearching ? breeds : [];
  const visibleBreeds = resultBreeds.slice(0, visibleCount);
  const selectedQuery = selected.find(
    (value): value is string =>
      typeof value === 'string' && value.toLocaleLowerCase() === trimmedQuery.toLocaleLowerCase(),
  );
  const selectionLimitReached =
    multiple && maxSelections !== undefined && new Set(selected).size >= maxSelections;
  const canSelectQuery =
    allowQuery &&
    isSearching &&
    (!multiple || selectedQuery === undefined) &&
    !selectionLimitReached;

  function queryLabel(value: string) {
    return (
      getQueryLabel?.(value) ??
      t('components:form.breed_input.all_matching_query', { query: value })
    );
  }

  function selectedValue(value: string | number) {
    return selected.find((item) =>
      typeof item === 'string' && typeof value === 'string'
        ? item.trim().toLocaleLowerCase() === value.trim().toLocaleLowerCase()
        : item === value,
    );
  }

  function selectValue(value: string | number, breed?: Breed) {
    const current = selectedValue(value);
    if (multiple && current !== undefined) {
      onRemove(current);
      return;
    }

    if (breed) {
      if (recordHistory) remember(breed.id);
      onToggle(breed);
    } else if (typeof value === 'string' && allowQuery && onSelectQuery) {
      const trimmedValue = value.trim();
      if (!trimmedValue) return;
      if (recordHistory) remember(trimmedValue);
      onSelectQuery(trimmedValue);
    }
  }

  function renderValue(value: string | number, { fromHistory = false, compact = false } = {}) {
    const breed =
      typeof value === 'number' ? allBreeds.find((breed) => breed.id === value) : undefined;
    const checked = selectedValue(value) !== undefined;
    const Component = compact ? BreedPickerSuggestion : BreedPickerRow;

    return (
      <Component
        key={`${typeof value}-${value}`}
        breed={breed}
        label={
          breed?.name ??
          (typeof value === 'string'
            ? compact
              ? value
              : queryLabel(value)
            : t('components:listings_filter_form.selected_breed'))
        }
        checked={checked}
        disabled={!checked && selectionLimitReached}
        multiple={multiple}
        testID={breed ? optionTestID?.(breed) : undefined}
        forgetTestID={fromHistory ? `breed-picker-forget-${value}` : undefined}
        onPress={() => selectValue(value, breed)}
        onForget={fromHistory ? () => forget(value) : undefined}
      />
    );
  }

  useEffect(() => setVisibleCount(40), [query]);

  return (
    <View className={'min-h-0 flex-1'}>
      <View className={'px-3 py-3 md:px-5'}>
        <TextInput
          ref={searchInputRef}
          dense={false}
          icon={falMagnifyingGlass}
          value={query}
          testID={inputTestID}
          autoFocus={autoFocus}
          placeholder={searchPlaceholder}
          accessibilityLabel={searchPlaceholder}
          onChangeText={setQuery}
          onSubmitEditing={() => canSelectQuery && selectValue(trimmedQuery)}
        />
      </View>
      <ScrollView
        className={'min-h-0 flex-1'}
        contentContainerClassName={'px-3 pb-6 md:px-5 md:pt-3'}
        keyboardShouldPersistTaps={'handled'}
        keyboardDismissMode={'interactive'}
      >
        {pinnedValues.length > 0 && (
          <View testID={'breed-filter-selected'} className={'mb-8'}>
            <Text className={'text-brand-brown-dark mb-3 font-bold'}>
              {t('components:form.breed_input.selected_breeds')}
            </Text>
            {pinnedValues.map((value) => renderValue(value))}
          </View>
        )}
        {allowQuery && isSearching && onSelectQuery && (
          <BreedPickerRow
            testID={queryOptionTestID}
            label={queryLabel(trimmedQuery)}
            checked={selectedQuery !== undefined}
            disabled={selectedQuery === undefined && selectionLimitReached}
            multiple={multiple}
            onPress={() => selectValue(trimmedQuery)}
          />
        )}
        {loading && breeds.length === 0 ? (
          <Loader dense={true} />
        ) : error && allBreeds.length === 0 ? (
          <View className={'items-center gap-3 py-6'}>
            <Text className={'text-brand-brown-dark/70 text-center'}>
              {t('common:notify_error_description')}
            </Text>
            {onRetry && (
              <Button
                text={t('common:retry')}
                color={'white'}
                dense={true}
                testID={retryTestID}
                onPress={onRetry}
              />
            )}
          </View>
        ) : !allowQuery && isSearching && breeds.length === 0 ? (
          <Text
            className={'text-brand-brown-dark/50 py-6 text-center'}
            accessibilityLiveRegion={'polite'}
          >
            {t('components:form.breed_input.no_results')}
          </Text>
        ) : (
          <>
            {historyValues.length > 0 && (
              <BreedPickerSection
                title={t('components:form.breed_input.selection_history')}
                testID={'breed-picker-history'}
                compact={true}
              >
                {historyValues.map((value) =>
                  renderValue(value, { fromHistory: true, compact: true }),
                )}
              </BreedPickerSection>
            )}
            {popularBreeds.length > 0 && (
              <BreedPickerSection
                title={t('components:form.breed_input.popular_breeds')}
                testID={'breed-picker-popular'}
                compact={true}
              >
                {popularBreeds.map((breed) => renderValue(breed.id, { compact: true }))}
              </BreedPickerSection>
            )}
            {visibleBreeds.length > 0 && (
              <BreedPickerSection testID={'breed-picker-all'}>
                {visibleBreeds.map((breed) => renderValue(breed.id))}
              </BreedPickerSection>
            )}
          </>
        )}
        {resultBreeds.length > visibleCount && (
          <TouchableOpacity
            className={'items-center py-4'}
            accessibilityRole={'button'}
            onPress={() => setVisibleCount((current) => current + 40)}
          >
            <Text className={'text-brand-brown-dark font-semibold underline'}>
              {t('common:view_more')}
            </Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </View>
  );
}

function BreedPickerSection({
  title,
  testID,
  compact = false,
  children,
}: {
  title?: string;
  testID: string;
  compact?: boolean;
  children: ReactNode;
}) {
  return (
    <View testID={testID} className={compact ? 'mb-6' : undefined}>
      {title && (
        <Text className={clsx('text-brand-brown-dark font-bold', !compact && 'mb-1')}>{title}</Text>
      )}
      {compact ? <View className={'mt-3 flex-row flex-wrap gap-1.5'}>{children}</View> : children}
    </View>
  );
}

type BreedPickerOptionProps = {
  breed?: Breed;
  label: string;
  checked: boolean;
  multiple: boolean;
  disabled?: boolean;
  testID?: string;
  onPress: () => void;
  onForget?: () => void;
  forgetTestID?: string;
};

function BreedPickerSuggestion({
  breed,
  label,
  checked,
  multiple,
  disabled = false,
  testID,
  onPress,
  onForget,
  forgetTestID,
}: BreedPickerOptionProps) {
  const { t } = useTranslation('components');

  return (
    <View
      accessible={false}
      className={clsx(
        'max-w-full shrink-0 flex-row items-center overflow-hidden rounded-full border bg-white p-1',
        checked ? 'border-brand-brown-dark' : 'border-brand-brown-dark/15',
        !checked && !disabled && 'hover:border-brand-brown-dark/30',
        disabled && 'opacity-50',
      )}
    >
      <Pressable
        testID={testID}
        className={'min-w-0 shrink flex-row items-center'}
        aria-checked={checked}
        accessibilityRole={multiple ? 'checkbox' : 'radio'}
        accessibilityLabel={label}
        accessibilityState={{ checked, disabled }}
        disabled={disabled}
        onPress={onPress}
      >
        {breed && (
          <Image
            src={breed.avatar}
            alt={breed.name}
            containerClassName={'size-6 shrink-0'}
            imageClassName={'rounded-full object-cover'}
            sizeType={'avatar'}
            width={24}
            sizes={'24px'}
          />
        )}
        <Text
          className={clsx('text-brand-brown-dark ml-1 shrink text-sm', onForget ? 'mr-2' : 'mr-1')}
        >
          {label}
        </Text>
      </Pressable>
      {onForget && (
        <Pressable
          testID={forgetTestID}
          accessibilityRole={'button'}
          accessibilityLabel={t('form.breed_input.remove_from_history', {
            breed: breed?.name ?? label,
          })}
          className={
            'border-brand-brown-dark/15 hover:border-brand-brown-dark/30 ml-1 rounded-full border p-1'
          }
          onPress={onForget}
        >
          <Icon icon={falTimes} className={'size-3'} />
        </Pressable>
      )}
    </View>
  );
}

function BreedPickerRow({
  breed,
  label,
  checked,
  multiple,
  disabled = false,
  testID,
  onPress,
}: BreedPickerOptionProps) {
  const { t: tCommon } = useTranslation('common');

  return (
    <View accessible={false} className={'-mx-1.5 flex-row items-center'}>
      <Pressable
        testID={testID}
        className={clsx(
          'min-w-0 flex-1 flex-row items-center gap-3 rounded-md border border-transparent bg-transparent p-1.5',
          disabled
            ? 'opacity-50'
            : 'hover:shadow-brand-brown-dark/5 group hover:bg-white hover:shadow-sm',
        )}
        aria-checked={checked}
        accessibilityRole={multiple ? 'checkbox' : 'radio'}
        accessibilityLabel={label}
        accessibilityState={{ checked, disabled }}
        disabled={disabled}
        onPress={onPress}
      >
        {breed ? (
          <Image
            src={breed.avatar}
            alt={breed.name}
            containerClassName={'size-12 flex-none'}
            imageClassName={'rounded object-cover'}
            sizeType={'avatar'}
          />
        ) : (
          <View className={'bg-brand-cream-dark size-12 items-center justify-center rounded'}>
            <Icon icon={fasMagnifyingGlass} className={'text-brand-brown-dark'} />
          </View>
        )}
        <View className={'min-w-0 flex-1'}>
          {breed && (
            <Text className={'text-sm text-stone-600'} numberOfLines={1}>
              {tCommon(`enums.animal.${breed.animal}`)}
            </Text>
          )}
          <Text
            className={clsx(
              'text-brand-brown-dark font-bold',
              !disabled && 'group-hover:underline',
            )}
            numberOfLines={breed ? 1 : undefined}
          >
            {label}
          </Text>
        </View>
        <View
          className={clsx(
            'size-5 items-center justify-center border',
            multiple ? 'rounded' : 'rounded-full',
            checked
              ? 'border-brand-brown-dark bg-brand-brown-dark'
              : 'border-brand-brown-dark/30 bg-white',
            !checked && !disabled && 'group-hover:border-brand-brown-dark',
          )}
        >
          {checked &&
            (multiple ? (
              <Icon icon={falCheck} className={'size-3 text-white'} />
            ) : (
              <View className={'size-2 rounded-full bg-white'} />
            ))}
        </View>
      </Pressable>
    </View>
  );
}
